// Dashboard — global ops view for the last 24 hours.

import { useCallback, useEffect, useState } from "react";

import ChartBar from "../components/ChartBar";
import { api, type DashboardResponse } from "../lib/api";
import {
  buttonStyle,
  Card,
  EmptyState,
  PageHeader,
  Skeleton,
  StatCard,
  tokens,
} from "../lib/theme";

const REASON_LABELS: Record<string, string> = {
  fresh_pull_full_price: "Accepted (full)",
  yield_throttled: "Accepted (partial throttle)",
  cache_hit: "Accepted (cached)",
  below_threshold_cached: "Below threshold (cached)",
  below_threshold_fresh: "Below threshold",
  no_match_upstream: "No match",
  upstream_timeout: "Upstream timeout",
  upstream_error: "Upstream error",
  upstream_invalid_input: "Invalid input",
  validation_failed: "Validation failed",
  rate_limited: "Rate limited",
  unauthorized: "Unauthorized",
  idempotent_replay: "Idempotent replay",
};

function fmtPct(n: number): string {
  return `${(n * 100).toFixed(1)}%`;
}

function fmtRps(n: number): string {
  if (n >= 100) return n.toFixed(0);
  if (n >= 10) return n.toFixed(1);
  return n.toFixed(2);
}

export default function Dashboard() {
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    api
      .dashboard()
      .then((d) => {
        setData(d);
        setError(null);
        setRefreshedAt(new Date());
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "Failed to load");
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <PageHeader
        title="Overview"
        subtitle="Last 24 hours"
        actions={
          <>
            {refreshedAt && (
              <span
                style={{
                  alignSelf: "center",
                  color: tokens.color.fg2,
                  fontSize: tokens.font.size.xs,
                }}
              >
                Updated{" "}
                {refreshedAt.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                })}
              </span>
            )}
            <button
              type="button"
              onClick={load}
              disabled={loading}
              style={buttonStyle("ghost", loading)}
            >
              {loading ? "Refreshing…" : "Refresh"}
            </button>
          </>
        }
      />

      {error && !data && (
        <Card>
          <EmptyState title="Failed to load" hint={error} />
        </Card>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: tokens.space.lg,
          marginBottom: tokens.space.xl,
        }}
      >
        {loading && !data ? (
          <>
            <SkeletonStat />
            <SkeletonStat />
            <SkeletonStat />
            <SkeletonStat />
          </>
        ) : data ? (
          <>
            <StatCard
              label="Throughput"
              value={fmtRps(data.rps)}
              hint="requests/sec (avg)"
              emphasis
            />
            <StatCard
              label="Accept rate"
              value={fmtPct(data.acceptRate)}
              hint="bid sent vs total"
            />
            <StatCard
              label="Cache hit rate"
              value={fmtPct(data.cacheHitRate)}
              hint="served from DynamoDB"
            />
            <StatCard
              label="Service errors"
              value={fmtPct(data.errorRate)}
              hint="upstream timeouts/errors"
            />
          </>
        ) : null}
      </div>

      <Card title="Top decisions" padding={tokens.space.lg}>
        {loading && !data ? (
          <Skeleton height={240} />
        ) : data && data.topReasons.length > 0 ? (
          <ChartBar
            data={data.topReasons.map((r) => ({
              x: REASON_LABELS[r.reason] ?? r.reason,
              count: r.count,
            }))}
            bars={[{ key: "count", name: "Requests" }]}
            layout="horizontal"
            height={Math.max(240, data.topReasons.length * 40 + 40)}
            showValues
          />
        ) : (
          <EmptyState
            title="No traffic in the last 24 hours"
            hint="Decisions will appear here once GTI starts sending requests."
          />
        )}
      </Card>
    </div>
  );
}

function SkeletonStat() {
  return (
    <div
      style={{
        background: tokens.color.bg2,
        border: `1px solid ${tokens.color.border}`,
        borderRadius: tokens.radius.lg,
        padding: tokens.space.lg,
      }}
    >
      <Skeleton height={10} width="40%" />
      <div style={{ height: tokens.space.sm }} />
      <Skeleton height={28} width="70%" />
      <div style={{ height: tokens.space.xs }} />
      <Skeleton height={10} width="50%" />
    </div>
  );
}
